/**
 * Example / future-scope pages.
 *
 * These are prototype-styled scaffolds for scopes we will build in later
 * increments (financing, payments, reviews, help, legal, etc.). Each renders
 * the shared PageScaffold + ComingSoon block with the planned scope, so
 * stakeholders can navigate the full information architecture today while the
 * functionality is planned. Routes are generated from EXAMPLE_PAGES in App.tsx.
 */
import React from 'react';

import { ComingSoon, PageScaffold } from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

export interface ExamplePageSpec {
  /** Route path (also the nav target). */
  path: string;
  /** Short human label (used for nav + the stakeholder list). */
  label: string;
  eyebrow: string;
  title: string;
  lead: string;
  /** Planned capabilities lifted from the prototype's intent. */
  features: string[];
  /** Source prototype filename for traceability. */
  prototype: string;
}

export const EXAMPLE_PAGES: ExamplePageSpec[] = [
  {
    path: '/guia-precios',
    label: 'Guía de precios',
    eyebrow: '★ Inteligencia de precios',
    title: 'Guía de precios',
    lead: 'Rangos de mercado por marca, modelo y año — para saber si un precio es justo antes de contactar.',
    features: [
      'Rango de mercado (mín · mediana · máx) por marca/modelo/año/versión',
      'Comparables recientes y su dispersión de precio',
      'Score de precio del listing (buen precio · en mercado · sobre mercado)',
      'Consume el motor de pricing (BC-04) cuando esté disponible',
    ],
    prototype: 'car-ia-guia-precios.html',
  },
  {
    path: '/como-funciona',
    label: 'Cómo funciona',
    eyebrow: '★ Para compradores y vendedores',
    title: 'Cómo funciona CAR-IA',
    lead: 'El recorrido de punta a punta: explorar con señales de confianza, contactar con privacidad y cerrar con seguridad.',
    features: [
      'Explora con Trust Signals y Decision Signals en cada listing',
      'Contacta sin exponer tu identidad hasta que decidas',
      'Publica tu vehículo con verificación de VIN y sugerencia de precio',
      'Compra segura con pasos de pago y garantías (scope futuro)',
    ],
    prototype: 'car-ia-como-funciona.html',
  },
  {
    path: '/financiamiento',
    label: 'Financiamiento',
    eyebrow: '★ Compra con financiamiento',
    title: 'Financiamiento',
    lead: 'Simula cuotas, compara opciones de financiamiento y pre-califícate con instituciones aliadas.',
    features: [
      'Simulador de cuota (monto, inicial, plazo, tasa)',
      'Comparativa de ofertas de instituciones financieras',
      'Pre-calificación y envío de solicitud',
      'Integración con partners de crédito (scope futuro)',
    ],
    prototype: 'car-ia-financiamiento.html',
  },
  {
    path: '/pagos',
    label: 'Pagos',
    eyebrow: '★ Pagos y monetización',
    title: 'Pagos',
    lead: 'Recargas de wallet, compra de leads y add-ons, y el historial de transacciones — sobre las rieles de pago BC-05.',
    features: [
      'Wallet y recarga (BC-05-E05)',
      'Compra de leads calientes y add-ons de listing',
      'Historial de pagos y recibos (ledger BC-05-E06)',
      'Pasarela CardNet / Azul detrás del ACL de pagos (BC-05-E11)',
    ],
    prototype: 'car-ia-pagos.html',
  },
  {
    path: '/resenas',
    label: 'Reseñas',
    eyebrow: '★ Reputación',
    title: 'Reseñas',
    lead: 'Reseñas de compradores y vendedores tras una transacción, para construir reputación en el marketplace.',
    features: [
      'Reseña post-transacción (comprador ↔ vendedor)',
      'Rating agregado por vendedor / dealer',
      'Moderación de reseñas y respuesta del vendedor',
      'Alimenta las señales de confianza (BC-03)',
    ],
    prototype: 'car-ia-resenas.html',
  },
  {
    path: '/compra-segura',
    label: 'Compra segura',
    eyebrow: '★ Confianza en la transacción',
    title: 'Compra segura',
    lead: 'El flujo de compra protegida: escrow de identidad, verificación y pasos de cierre seguros.',
    features: [
      'Escrow de información: identidad revelada solo al pagar el lead',
      'Checklist de verificación pre-cierre',
      'Pasos de pago y liberación de fondos (scope futuro)',
      'Reembolso y disputa (BC-05-E09)',
    ],
    prototype: 'car-ia-compra-segura.html',
  },
  {
    path: '/tasacion',
    label: 'Tasación',
    eyebrow: '★ Vende tu auto',
    title: 'Tasación instantánea',
    lead: 'Obtén una tasación de referencia de tu vehículo en segundos y publícalo con un precio sugerido.',
    features: [
      'Tasación por marca/modelo/año/kilometraje/estado',
      'Rango de precio sugerido y confianza',
      'Publicación directa con el precio sugerido',
      'Consume el motor de pricing (BC-04)',
    ],
    prototype: 'car-ia-tasacion.html',
  },
  {
    path: '/compras',
    label: 'Mis compras',
    eyebrow: '★ Mi actividad',
    title: 'Mis compras',
    lead: 'El historial de tus compras, leads adquiridos y contactos revelados en un solo lugar.',
    features: [
      'Listado de leads comprados y contactos revelados',
      'Estado de cada transacción',
      'Recibos y comprobantes',
      'Acceso a soporte por compra',
    ],
    prototype: 'car-ia-compras.html',
  },
  {
    path: '/ayuda',
    label: 'Ayuda',
    eyebrow: '★ Centro de ayuda',
    title: 'Ayuda',
    lead: 'Preguntas frecuentes, guías y canales de soporte para compradores y vendedores.',
    features: [
      'FAQ por categoría (comprar, vender, pagos, confianza)',
      'Guías paso a paso',
      'Contacto con soporte',
      'Estado del servicio',
    ],
    prototype: 'car-ia-ayuda.html',
  },
  {
    path: '/legal',
    label: 'Legal',
    eyebrow: '★ Términos y privacidad',
    title: 'Legal',
    lead: 'Términos de uso, política de privacidad y avisos legales del marketplace.',
    features: [
      'Términos y condiciones',
      'Política de privacidad y manejo de datos',
      'Política de cookies',
      'Avisos y cumplimiento',
    ],
    prototype: 'car-ia-legal.html',
  },
];

export const ExamplePage: React.FC<{ spec: ExamplePageSpec }> = ({ spec }) => {
  const authChrome = useAuthChrome();
  return (
    <PageScaffold
      {...authChrome}
      eyebrow={spec.eyebrow}
      title={spec.title}
      lead={spec.lead}
      width="narrow"
    >
      <ComingSoon
        title={`${spec.label} — planificado`}
        lead={spec.lead}
        features={spec.features}
        prototype={spec.prototype}
      />
    </PageScaffold>
  );
};

export default ExamplePage;
